import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import CountUp from "react-countup";
import "react-toastify/dist/ReactToastify.css";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Input,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
} from "reactstrap";
import {
    NumberParam,
    StringParam,
    useQueryParams,
    withDefault,
} from "use-query-params";
//
import CollapseFilter from "components/Common/CollapseFilter";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import LabelWrapper from "components/Common/LabelWrapper";
import {
    CATEGORY_STATUS,
    COLOR_CATEGORY_STATUS,
    LIST_OPTION_APPLICATION_STATUS,
} from "helpers/constans";
import { formatDateStringToKorea, formatDateStringToUTC } from "helpers/format";
import { useRole } from "components/Hooks/UserHooks";
import BreadCrumb from "components/Common/BreadCrumb";
import { useSlice } from "components/Hooks/useSlice";
import { ICategory, ICategoryQuery, ICategoryTranslation, IOrderCategoryPayload } from "api/types/_category";
import { ICategoryState, updateCategory, updateCategoryStatus } from "store/category/reducer";
import { deleteCategory, getCategories, orderCategory } from "store/category/thunk";
import TooltipCustom from "components/Common/TooltipCustom";
import CheckboxStatus from "./CheckboxStatus";
import TableDragContainer from "components/Common/TableDragContainer";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import ModalConfirm from "components/Common/ModalConfirm";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import CategoryForm from "./CategoryForm";
import { useAppDispatch } from "components/Hooks/useAppDispatch";

registerLocale("en", en);
registerLocale("ko", ko);

const CategoryManagement = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch()
    const { userPermissions } = useRole();
    const DEFAULT_PAGE = 1;
    const DEFAULT_LIMIT = 1000; //not limit in api
    const queryConfig = {
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, DEFAULT_PAGE),
        limit: withDefault(NumberParam, DEFAULT_LIMIT),
        title: withDefault(StringParam, ""),
        is_active: withDefault(StringParam, ""),
        created_to: withDefault(StringParam, ""),
        created_from: withDefault(StringParam, ""),
    };
    const [query, setQuery] = useQueryParams(queryConfig);
    const [titleSearch, setTitleSearch] = useState(query.title ?? "");
    const [statusSearch, setStatusSearch] = useState(
        LIST_OPTION_APPLICATION_STATUS.find((item) => item.value === query.is_active)?.value ?? ""
    );
    const [dateSearch, setDateSearch] = useState<[Date | null, Date | null]>([
        query.created_from ? moment(query.created_from, "Y-MM-DD").toDate() : null,
        query.created_to ? moment(query.created_to, "Y-MM-DD").toDate() : null,
    ]);
    const [itemToDelete, setItemToDelete] = useState<ICategory | null>(null);
    const [isLoadingDelete, setIsLoadingDelete] = useState(false);
    const [startDate, endDate] = dateSearch;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [categoryEdit, setCategoryEdit] = useState<
        ICategory | null
    >(null);

    const { categories, pagination, isLoading, fetch } = useSlice<
        ICategoryState,
        ICategoryQuery,
        any
    >({
        sliceName: "Category",
        thunk: getCategories,
    });


    const searchData = () => {
        setQuery({
            ...query,
            title: titleSearch.trim(),
            is_active: statusSearch,
            created_from: startDate
                ? formatDateStringToUTC(moment(startDate).startOf("day").format("YYYY-MM-DD HH:mm:ss"))
                : "",
            created_to: endDate
                ? formatDateStringToUTC(moment(endDate).endOf("day").format("YYYY-MM-DD HH:mm:ss"))
                : "",
            page: DEFAULT_PAGE,
            time_request: Date.now(),
        });
    };

    const resetData = () => {
        setQuery(
            {
                title: "",
                is_active: "",
                created_from: "",
                created_to: "",
                page: DEFAULT_PAGE,
                time_request: Date.now(),
            },
            "push"
        );
        setTitleSearch("");
        setStatusSearch("");
        setDateSearch([null, null]);
    };

    const handleChangePicker = (values: [Date | null, Date | null] = [null, null]) => {
        setDateSearch(values);
    };

    const handleOrderList = async (values: ICategory[]) => {
        if (!values?.length) return;
        const params = values?.map((item, index) => {
            return {
                id: item.id,
                order: index + 1
            }
        }) as IOrderCategoryPayload[];
        const response = await orderCategory(params);
        if (typeof response === "string") {
            toast(response, CONFIG_OPTION_TOAST_ERROR);
            return;
        }

        if (response.statusCode !== 200) {
            toast(response.message || "Error", CONFIG_OPTION_TOAST_ERROR);
            return;
        }
        dispatch(updateCategory({ data: values }));
    };

    const handleActionDelete = async (id: number) => {
        setIsLoadingDelete(true);
        try {
            const response = await deleteCategory(id);

            if (typeof response === "string") {
                toast(response, CONFIG_OPTION_TOAST_ERROR);
                return;
            }

            if (response.statusCode !== 200) {
                toast(response.message || "Error", CONFIG_OPTION_TOAST_ERROR);
                return;
            }
            setItemToDelete(null);
            resetData();
            toast(t("The process has been completed."), CONFIG_OPTION_TOAST_NORMAL);
        } catch (error) {
            toast("Unexpected error occurred", CONFIG_OPTION_TOAST_ERROR);
        } finally {
            setIsLoadingDelete(false);
        }
    };

    const handleChangeActive = (id: number, active: boolean) => {
        dispatch(updateCategoryStatus({ id, data: { is_active: active } }));
    };

    const onCloseClick = () => {
        setCategoryEdit((_prev) => null);
        setIsOpen((_prev) => false);
    };

    const columns = useMemo(
        () => [
            {
                Header: t("Order"),
                thWidth: 10,
                thClass: "whitespace-nowrap",
                Cell: () => {
                    return (
                        <div className="handle" style={{ cursor: "move", textAlign: "center", width: "30px", height: "30px" }}>
                            <i className="ri ri-drag-move-2-line fs-20"></i>
                        </div>
                    );
                },
            },
            {
                Header: t("Status"),
                accessor: "is_active",
                thWidth: 150,
                filterable: false,
                sortable: false,
                thClass: "text-center",
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return <CheckboxStatus item={item} onChange={handleChangeActive} />;
                },
            },
            {
                Header: t("Category Name"),
                accessor: "translations",
                filterable: true,
                sortable: false,
                thClass: "whitespace-nowrap",
                Cell: ({ value }: { value: ICategoryTranslation[] }) => {
                    return (
                        <div className="cursor-pointer">
                            {value?.find((item) => item?.lang === i18n.language)?.title ?? "-"}
                        </div>
                    );
                },
            },
            {
                Header: t("Created at"),
                accessor: "created_at",
                filterable: true,
                sortable: false,
                thWidth: 200,
                thClass: "text-left",
                Cell: (cell: any) => {
                    const date = formatDateStringToKorea(cell?.value || "");
                    const arrDate = String(date || "").split(" ");

                    return (
                        <div className="text-left" style={{ minWidth: "100px" }}>
                            <span>
                                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                            </span>
                        </div>
                    );
                },
            },
            {
                Header: t("Updated at"),
                accessor: "updated_at",
                filterable: true,
                sortable: false,
                thWidth: 200,
                thClass: "text-left",
                Cell: (cell: any) => {
                    const date = formatDateStringToKorea(cell?.value || "");
                    const arrDate = String(date || "").split(" ");

                    return (
                        <div className="text-left" style={{ minWidth: "100px" }}>
                            <span>
                                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                            </span>
                        </div>
                    );
                },
            },
            {
                Header: t("action"),
                thClass: "text-center",
                thWidth: 100,
                Cell: (cell: any) => {
                    const item: ICategory = cell?.row?.original;
                    return (
                        <ul
                            className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0"
                            style={{ minWidth: "70px" }}
                        >
                            {
                                isHavePermissionRole(
                                    ROLES_FOR_APP.CATEGORY_UPDATE,
                                    userPermissions
                                ) && <TooltipCustom
                                    title={t("Button Update Category")}
                                    id={`update-us-${item?.id}`}
                                >
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-soft-primary edit-item-btn"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setCategoryEdit(item);
                                        }}
                                    >
                                        <i className="ri-pencil-fill align-bottom"></i>
                                    </button>
                                </TooltipCustom>
                            }

                            {
                                isHavePermissionRole(
                                    ROLES_FOR_APP.CATEGORY_DELETE,
                                    userPermissions
                                ) && <TooltipCustom
                                    title={t("Button Delete Category")}
                                    id={`detail-cp-${item?.id}`}
                                >
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-soft-danger edit-item-btn"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setItemToDelete(item);
                                        }}
                                    >
                                        <i className="ri-delete-bin-3-fill align-bottom"></i>
                                    </button>
                                </TooltipCustom>
                            }
                        </ul>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [i18n?.language, pagination?.total, query?.page]
    );

    useEffect(() => {
        fetch(query);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(query)]);

    useEffect(() => {
        document.title = `${t("Category Management")} | ShowStar`;
        document.body.classList.remove("vertical-sidebar-enable");
    }, [i18n.language, t]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        title={t("Category Management")}
                        pageTitle={t("Home")}
                    />
                    <Row>
                        <Col lg={12}>
                            <Card
                                id="customerList"
                                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
                            >
                                <CardHeader className="border-0">
                                    <Row className="g-4 align-items-center">
                                        <div className="col-5 con-sm-6">
                                            <div>
                                                <h5 className="card-title mb-0">
                                                    <span className="me-2">{t("Total")}:</span>
                                                    <CountUp
                                                        start={0}
                                                        end={pagination?.total || 0}
                                                        duration={1}
                                                        className="text-primary"
                                                    />
                                                </h5>
                                            </div>
                                        </div>
                                        <div className="col-7 con-sm-6 text-end">
                                            {isHavePermissionRole(
                                                ROLES_FOR_APP.CATEGORY_CREATE,
                                                userPermissions
                                            ) && (
                                                    <div className="col-sm-auto">
                                                        <div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-success add-btn"
                                                                id="create-btn"
                                                                onClick={() => setIsOpen((_prev) => true)}
                                                            >
                                                                <i className="ri-add-line align-bottom me-1"></i>
                                                                {t("Button Create Category")}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    </Row>
                                </CardHeader>

                                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                                    <CollapseFilter>
                                        <Row className="g-4 align-items-center mt-0 mt-md-2">
                                            <Col
                                                sm={12}
                                                md={3}
                                                xl={3}
                                                xxl={3}
                                                className="mt-3 mt-md-2"
                                            >
                                                <LabelWrapper
                                                    label={`${t("Category Name")}`}
                                                    isShow={!!titleSearch}
                                                >
                                                    <Input
                                                        type="text"
                                                        className="form-control search"
                                                        placeholder={`${t("Category Name")}...`}
                                                        value={titleSearch}
                                                        onChange={(e) => setTitleSearch(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                searchData();
                                                            }
                                                        }}
                                                    />
                                                </LabelWrapper>
                                            </Col>

                                            <Col
                                                sm={12}
                                                md={3}
                                                xl={3}
                                                xxl={3}
                                                className="mt-3 mt-md-3"
                                            >
                                                <LabelWrapper label={t("status")} isShow={true}>
                                                    <DropdownStatusNew
                                                        name="status"
                                                        dataList={CATEGORY_STATUS || []}
                                                        placeholder={`${t("status")}...`}
                                                        className="dropdown-status-rounded"
                                                        classNamePrefix="name-prefix"
                                                        initialValue={statusSearch}
                                                        onChangeSelect={(e: any) =>
                                                            setStatusSearch(e?.value)
                                                        }
                                                        isHasOptionAll={true}
                                                        optionAll={{ label: t("All Filter"), value: "" }}
                                                        colors={COLOR_CATEGORY_STATUS}
                                                    />
                                                </LabelWrapper>
                                            </Col>

                                            <Col
                                                sm={12}
                                                md={3}
                                                xl={3}
                                                xxl={3}
                                                className="mt-sm-3 mt-md-3 mt-3 date-picker-wrapper-custom"
                                            >
                                                <LabelWrapper
                                                    label={t("Date")}
                                                    isShow={!!startDate || !!endDate}
                                                >
                                                    <DatePickerCustom
                                                        startDate={startDate || null}
                                                        endDate={endDate || null}
                                                        onChangePicker={handleChangePicker}
                                                    />
                                                </LabelWrapper>
                                            </Col>

                                            <Col
                                                sm={12}
                                                md={12}
                                                xl={3}
                                                xxl={3}
                                                className="hstack gap-1 justify-content-xl-start justify-content-md-start mt-sm-4 mt-md-3"
                                            >
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary me-1"
                                                        onClick={searchData}
                                                    >
                                                        <i className="ri-search-line align-bottom me-1"></i>{" "}
                                                        {t("Button Search")}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary fs-14"
                                                        onClick={resetData}
                                                    >
                                                        <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                                        {t("Button Reset")}
                                                    </button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CollapseFilter>
                                </div>
                                <CardBody className="pt-3">
                                    <div>
                                        <TableDragContainer
                                            columns={columns}
                                            data={categories?.length ? categories : []}
                                            className="custom-header-css"
                                            divClass="table-responsive table-card"
                                            tableClass="align-middle table-bordered-dashed"
                                            theadClass="table-light text-muted"
                                            onOrderChange={handleOrderList}
                                            handleClassName="handle"
                                            lockedClassName="locked_item"
                                            isLoading={isLoading}
                                        />
                                    </div>
                                    <ToastContainer closeButton={false} limit={1} />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
            {itemToDelete && (
                <ModalConfirm
                    header={t("Button Delete Category")}
                    isOpen={!!itemToDelete}
                    isLoading={isLoadingDelete}
                    onClose={() => setItemToDelete(null)}
                    onConfirm={() => handleActionDelete(itemToDelete.id)}
                />
            )}
            <Modal
                isOpen={!!categoryEdit?.id || isOpen}
                centered={true}
                size="lg"
                scrollable={true}
            >
                <ModalHeader toggle={onCloseClick}>
                    <>
                        {!!categoryEdit?.id
                            ? t("Button Update Category")
                            : t("Button Create Category")}
                    </>{" "}
                    {categoryEdit?.id ? ` - ${categoryEdit?.translations?.find((i: any) => i.lang === i18n.language)?.title || categoryEdit?.title}` : null}
                </ModalHeader>
                <ModalBody>
                    <div>
                        <CategoryForm
                            item={categoryEdit}
                            triggerRefresh={() => {
                                onCloseClick();
                                fetch(query)
                            }}
                        />
                    </div>
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default CategoryManagement;
